import { mapPlayerToForm } from '../../profile/player'
import type { PlayerProfileResponse } from '../../../services/player/playerProfile'
import type { RosterPlayerFormValues } from './types'
import type { PlayerSuspensionSource } from './utils/playerStatus'

/**
 * A roster player row, as returned in the `players[]` array of
 * GET /teams/:team/rosters/:roster/players. A superset of PlayerProfileResponse
 * (same snake_case personal fields + image) PLUS the roster-specific fields.
 * Only the roster fields are typed here; the personal fields are read via
 * PlayerProfileResponse when mapping.
 */
export type RosterPlayerListItem = PlayerProfileResponse & {
  player_number?: number | string | null
  player_type?: { id?: string | number | null } | null
  is_affiliate?: boolean | null
  is_injured?: boolean | null
  customer_identifier?: string | null
  /** Canlan-only Salesforce ID. */
  sf_id?: string | null
  /** Invite was sent (player has a pending/active invitation). */
  is_invited?: boolean | null
  /**
   * Player accepted the invite. Once accepted the email is the player's own
   * identity and must not be edited from the roster; while still unaccepted
   * (is_invited && !is_accepted) the email remains editable.
   */
  is_accepted?: boolean | null
  /** Signed the waiver; shown in the roster's Signed Waiver column. */
  is_signed_waiver?: boolean | null
  /** The player's suspensions; drives the roster Status column. */
  suspensions?: PlayerSuspensionSource[] | null
}

const str = (v: unknown): string =>
  v === null || v === undefined ? '' : String(v)

/**
 * Map a roster player row to the roster edit form values. Personal fields reuse
 * the shared profile `mapPlayerToForm` (the row carries the same snake_case
 * personal keys + image as the /me/players response). Roster fields are read
 * straight off the row. Used to prefill the Edit Player modal WITHOUT a separate
 * fetch — the roster list row already holds everything.
 */
export const mapRosterPlayerToForm = (
  player: RosterPlayerListItem
): RosterPlayerFormValues => ({
  ...mapPlayerToForm(player),
  playerNumber: str(player.player_number),
  positionId: str(player.player_type?.id),
  isAffiliate: Boolean(player.is_affiliate),
  isInjured: Boolean(player.is_injured),
  customerIdentifier: str(player.customer_identifier),
  salesforceId: str(player.sf_id),
})
